import axios from "axios";
import React, { useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import "../styles/checkout-page.css";
import { getTemplateData } from "../services/apiservice";
import Footer from "./Footer";
import PackModal from "./PackModal";
import "../styles/Multi-pack-list.css";
import GradientHeader from "./GradientHeader";
import { height } from "@mui/system";

// Utility function to get product image with correct priority
const getProductImage = (product) => {
  const defaultImage =
    "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";

  // 1st Priority: Mockup images with priority handling
  if (product?.mockup_images?.length > 0) {
    // Check for priority 1 mockups first
    const priority1Mockup = product.mockup_images.find(
      (img) => img.priority === 1
    );
    if (priority1Mockup) {
      return priority1Mockup.image_url;
    }
    // If no priority 1, use first mockup (priority 0)
    return product.mockup_images[0].image_url;
  }

  // 2nd Priority: Product images
  if (product?.images?.length > 0) {
    return product.images[0].image_url;
  }

  // 3rd Priority: Default
  return defaultImage;
};

// Determine the display image for a swagpack when products length is exactly 1
const resolvePackDisplayImage = (pack) => {
  const fallback =
    "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";

  if (Array.isArray(pack?.products) && pack.products.length === 1) {
    const singleProduct = pack.products[0];
    if (singleProduct?.mockup_images?.length > 0) {
      const mockupP1 = singleProduct.mockup_images.find(
        (m) => m.priority === 1
      );
      if (mockupP1?.image_url) return mockupP1.image_url;
      return (
        singleProduct.mockup_images[0]?.image_url ||
        pack.pack_image_url ||
        fallback
      );
    }
    if (singleProduct?.images?.length > 0) {
      return (
        singleProduct.images[0]?.image_url || pack.pack_image_url || fallback
      );
    }
    return pack.pack_image_url || fallback;
  }
  return pack.pack_image_url || fallback;
};

// Generate a collage component for multiple products
const ProductCollage = ({ products, maxProducts = 4 }) => {
  const fallback =
    "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";

  // Get images for up to maxProducts
  const productImages = products
    .slice(0, maxProducts)
    .map((product) => getProductImage(product));

  // Determine grid layout based on number of products
  const getGridClass = (count) => {
    if (count === 1) return "collage-single";
    if (count === 2) return "collage-double";
    if (count === 3) return "collage-triple";
    return "collage-quad"; // 4 or more
  };

  const gridClass = getGridClass(productImages.length);

  return (
    <div className={`product-collage ${gridClass}`}>
      {productImages.map((imageUrl, index) => {
        const isLastImage = index === productImages.length - 1;
        const hasMoreProducts = products.length > maxProducts;

        return (
          <div key={index} className="collage-item">
            <img
              src={imageUrl}
              alt={`Product ${index + 1}`}
              className="collage-img"
              style={{
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.src = fallback;
              }}
            />
            {/* Show "+X more" overlay on the last image if there are more products */}
            {isLastImage && hasMoreProducts && (
              <div className="more-overlay">
                <span className="more-indicator">
                  +{products.length - maxProducts}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const CheckoutPage = () => {
  const [checkoutDetails, setCheckoutDetails] = useState({
    backroundColor: "",
    logoImageUrl: "",
    buttonColor: "",
    has_multiple_choice: "",
    footerColor: "",
    footerText: "",
    customerId: null,
    headerColor: "",
    pageContent1: "",
    pageContent2: "",
    singlePackHeading: "",
    multiplePackHeading: "",
    multiplePackChoiceHeading: "",
    multiplePackContent: "",
    multiplePackChoiceContent: "",
  });
  const queryParameters = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [showProducts, setShowProducts] = useState(1);
  const [open, setOpen] = useState(false);
  const [swagpacks, setSwagpacks] = useState([]);
  const [showPackDetails, setShowPackDetails] = useState();
  const [selectedPack, setSelectedPack] = useState(null);
  const [preSelectedProduct, setPreSelectedProduct] = useState(null);
  const [selectedPackIndex, setSelectedPackIndex] = useState(null);
  const token = queryParameters.get("token");
  const navigate = useNavigate();
  let params = { token: token };
  const [customerId, setCustomerId] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [packContainerRef, setPackContainerRef] = useState(null);

  useEffect(() => {
    if (token === null || token === "" || token === undefined) {
      setLoading(false);
      navigate("/");
      return;
    }
    getCouponItems();
  }, []);

  // Add new useEffect to handle direct navigation
  useEffect(() => {
    if (!loading && showProducts === 0 && swagpacks.length === 1) {
      // If there's only one pack and products shouldn't be shown, navigate directly
      navigate(`/redemption/details?token=${token}`, {
        state: { selectedPack: { selectedPack: [swagpacks[0]] } },
      });
    }
  }, [loading, showProducts, swagpacks]);

  function getTemplateIdFromCoupon() {
    getTemplateData()
      .then((e) => {
        setCheckoutDetails({
          backgroundColor: e.data.result.pageBackgroundColor,
          logoImageUrl: e.data.result.logo_image_url,
          buttonColor: e.data.result.buttonColor,
          has_multiple_choice: e.data.result.has_multiple_choice,
          footerColor: e.data.result.footer_color || "",
          footerText: e.data.result.footer_text || "",
          customerId: e.data.result.customer_id ?? null,
          headerColor: e.data.result.header_color || "",
          pageContent1: e.data.result.product_page_content_1 || "",
          pageContent2: e.data.result.product_page_content_2 || "",
          singlePackHeading:
            e.data.result.product_page_heading_single_pack || "",
          multiplePackHeading:
            e.data.result.product_page_heading_multiple_pack || "",
          multiplePackChoiceHeading:
            e.data.result.product_page_heading_multiple_pack_choice || "",
          multiplePackContent:
            e.data.result.product_page_heading_multiple_pack_content || "",
          multiplePackChoiceContent:
            e.data.result.product_page_heading_multiple_pack_choice_content ||
            "",
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getTemplateIdFromCoupon();
  }, [swagpacks]);

  function getCouponItems() {
    axios
      .get(
        `${process.env.REACT_APP_URL}/v4/customer/redemption/couponItems?token=${token}`
      )
      .then((res) => {
        if (res.data.success && res.data.result) {
          if (
            res.data.result.swagpacks &&
            res.data.result.swagpacks.length > 0
          ) {
            setSwagpacks(res.data.result.swagpacks);
            setShowProducts(res.data.result.show_products);
            setCustomerId(res.data.result.customer_id);
            // Direct navigation when showProducts is 0
            if (res.data.result.show_products === 0) {
              setLoading(false);
              navigate(`/redemption/details?token=${token}`, {
                state: {
                  selectedPack: {
                    selectedPack:
                      res.data.result.swagpacks.length === 1
                        ? [res.data.result.swagpacks[0]]
                        : res.data.result.swagpacks,
                  },
                },
              });
              return;
            }
          } else {
            setLoading(false);
            navigate({
              pathname: "/",
              search: `?${createSearchParams(params)}`,
            });
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        navigate({
          pathname: "/",
          search: `?${createSearchParams(params)}`,
        });
      });
  }

  const handlePackSelectionChange = (pack, index) => {
    setSelectedPack({
      selectedPack: [pack],
    });
    setSelectedPackIndex(index);
  };

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setShowPackDetails(null);
    setPreSelectedProduct(null);
  };

  const handleProductClick = (product, index) => {
    // When user clicks on a product, show the PackModal with the swagpack details
    // and pre-select the clicked product (works for any swagpack count)
    setShowPackDetails(swagpacks[0]);
    setPreSelectedProduct({ ...product, originalIndex: index });
    openModal();
  };

  const handleRoute = () => {
    if (checkoutDetails.has_multiple_choice === 1) {
      if (swagpacks.length > 1 && !selectedPack) {
        console.log("No pack selected");
        return;
      }

      if (swagpacks.length === 1) {
        navigate(`/redemption/details?token=${params.token}`, {
          state: {
            selectedPack: {
              selectedPack: swagpacks,
            },
          },
        });
      } else {
        navigate(`/redemption/details?token=${params.token}`, {
          state: { selectedPack },
        });
      }
    } else {
      setSelectedPack({ selectedPack: swagpacks });
      navigate(`/redemption/details?token=${params.token}`, {
        state: { selectedPack: { selectedPack: swagpacks } },
      });
    }
  };

  const showPackProducts = (pack) => {
    if (showProducts === 0) {
      setSelectedPack({
        selectedPack: [pack],
      });
      navigate(`/redemption/details?token=${params.token}`, {
        state: { selectedPack: { selectedPack: [pack] } },
      });
      return;
    }
    if (showProducts === 1) {
      setShowPackDetails(pack);
      // Auto-select first product when pack is clicked to go directly to detailed view
      if (pack?.products?.length > 0) {
        setPreSelectedProduct({ ...pack.products[0], originalIndex: 0 });
      }
      openModal();
    }
  };

  // Arrow navigation functions
  const scrollLeft = () => {
    if (packContainerRef) {
      packContainerRef.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (packContainerRef) {
      packContainerRef.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  // Check if arrows should be visible based on scroll position and content overflow
  const checkArrowsVisibility = () => {
    if (packContainerRef) {
      const { scrollLeft, scrollWidth, clientWidth } = packContainerRef;
      const hasOverflow = scrollWidth > clientWidth;

      setShowLeftArrow(hasOverflow && scrollLeft > 5);
      setShowRightArrow(
        hasOverflow && scrollLeft < scrollWidth - clientWidth - 5
      );
    }
  };

  // Set up scroll event listener and resize observer
  useEffect(() => {
    if (packContainerRef) {
      // Initial check with a small delay to ensure DOM is fully rendered
      const initialCheck = setTimeout(() => {
        checkArrowsVisibility();
      }, 100);

      packContainerRef.addEventListener("scroll", checkArrowsVisibility);

      // Also check on window resize
      const handleResize = () => {
        checkArrowsVisibility();
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(initialCheck);
        if (packContainerRef) {
          packContainerRef.removeEventListener("scroll", checkArrowsVisibility);
        }
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [packContainerRef, swagpacks]);

  return (
    <div className="m-0 p-0">
      {loading ? (
        <div className="loading-indicator-admin"> </div>
      ) : (
        <div className="d-flex flex-column min-vh-100">
          <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0">
            <div
              // className={
              //   customerId &&
              //   Number(customerId) === Number(process.env.REACT_APP_CUSTOMER_ID)
              //     ? "top-margin-two light-header"
              //     : "top-margin-two header"
              // }
              className="top-margin-two header"
              style={{
                background: checkoutDetails.headerColor || "#1E1665",
              }}
            >
              {checkoutDetails.logoImageUrl && (
                <div className="d-flex">
                  <GradientHeader
                    logoSrc={checkoutDetails.logoImageUrl}
                    justifyContent="flex-start"
                    headerColor={checkoutDetails.headerColor}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="outer-div col-12 flex-grow-1">
            <div className="row inner-div" style={{ maxWidth: "1500px" }}>
              <div className="col-12 mt-3 mb-1 p-3">
                <h1 className="pack-heading d-flex justify-content-center">
                  {swagpacks.length === 1 && showProducts === 1 ? (
                    checkoutDetails.singlePackHeading !== " " && (
                      <span>
                        {checkoutDetails.singlePackHeading ||
                          "Products in your pack"}
                      </span>
                    )
                  ) : checkoutDetails.has_multiple_choice === 1 ? (
                    checkoutDetails.multiplePackChoiceHeading !== " " && (
                      <span>
                        {checkoutDetails.multiplePackChoiceHeading ||
                          "Here's Your Perfect Packs"}
                      </span>
                    )
                  ) : checkoutDetails.multiplePackHeading !== " " ? (
                    <span>
                      {checkoutDetails.multiplePackHeading ||
                        "Pick Your Perfect Pack"}
                    </span>
                  ) : (
                    "Here's Your Perfect Pack"
                  )}
                </h1>
                <h6 className="pack-subheading d-flex justify-content-center text-center flex-column">
                  {swagpacks.length === 1 && showProducts === 1 ? (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "We're delighted to share with you the contents of your special Swag pack."}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 ||
                            "Below is the list of products included. We hope you'll find joy and satisfaction in each item."}
                        </span>
                      )}
                    </>
                  ) : checkoutDetails.has_multiple_choice === 1 ? (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "You've received a selection of gift, carefully curated for you. Please select your preferred option from the list below"}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 || ""}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "Enjoy your personally curated swag pack — thoughtfully selected and carefully crafted."}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 || ""}
                        </span>
                      )}
                    </>
                  )}
                </h6>
              </div>

              <div className="pack-container-wrapper position-relative">
                {/* Left Arrow */}
                {showLeftArrow && (
                  <button
                    className="scroll-arrow scroll-arrow-left"
                    onClick={scrollLeft}
                    aria-label="Scroll left"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}

                {/* Right Arrow */}
                {showRightArrow && (
                  <button
                    className="scroll-arrow scroll-arrow-right"
                    onClick={scrollRight}
                    aria-label="Scroll right"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}

                <div className="row pack-container" ref={setPackContainerRef}>
                  {swagpacks.length === 1 && showProducts === 1
                    ? // Show products in the same layout as packs
                      swagpacks[0].products.map((product, index) => (
                        <div
                          className="col-xl-3 col-lg-3 col-md-3 col-sm-4 col-xs-6 col-6 pb-3"
                          key={index}
                        >
                          <div className="col text-center">
                            <div className="rounded product-card border-none border-0 p-3 h-100">
                              <div
                                className=""
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleProductClick(product, index)
                                }
                              >
                                <img
                                  src={getProductImage(product)}
                                  className="mockimg img-fluid"
                                  style={{ maxHeight: "150px" }}
                                  alt={`Product ${index}`}
                                  onError={(e) => {
                                    e.target.src =
                                      "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";
                                  }}
                                />
                              </div>
                            </div>
                            <div className="pack-name border-0 pt-2 d-flex justify-content-start">
                              <label
                                className="card-title p-0"
                                style={{
                                  fontWeight: "400",
                                  fontFamily: "Poppins",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  fontSize: "1rem",
                                }}
                                title={product.product_name}
                              >
                                {product.product_name}
                              </label>
                            </div>
                          </div>
                        </div>
                      ))
                    : // Show packs with selection if has_multiple_choice is 1
                      swagpacks.map((pack, index) => (
                        <div
                          className="col-xl-3 col-lg-3 col-md-3 col-sm-4 col-xs-6 col-6 pb-3"
                          key={index}
                        >
                          <div className="col text-center">
                            <div
                              className={`rounded product-card border-none border-0 p-4 ${
                                selectedPackIndex === index ? "selected" : ""
                              }`}
                              style={{ position: "relative" }}
                              onClick={() =>
                                checkoutDetails.has_multiple_choice === 1 &&
                                handlePackSelectionChange(pack, index)
                              }
                            >
                              {checkoutDetails.has_multiple_choice === 1 && (
                                <div className="pack-selection">
                                  <div
                                    className={`selection-circle ${
                                      selectedPackIndex === index
                                        ? "selected"
                                        : ""
                                    }`}
                                  >
                                    {selectedPackIndex === index && (
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              )}
                              <div
                                className="img-div"
                                style={{
                                  cursor:
                                    showProducts === 1 ? "pointer" : "default",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showProducts === 1 && showPackProducts(pack);
                                }}
                              >
                                <ProductCollage products={pack.products} />
                              </div>
                            </div>
                            <div className="pack-name border-0 pt-2 d-flex justify-content-start">
                              <label
                                className="card-title p-0"
                                style={{
                                  fontWeight: "400",
                                  fontFamily: "Poppins",
                                }}
                                title={pack.label}
                              >
                                {pack.label}
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                </div>
              </div>

              <div className="row p-3 ">
                <div
                  id="checkoutbtn"
                  className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 col-12 redeem-btn-div d-flex justify-content-center align-items-center"
                >
                  <button
                    onClick={() => handleRoute()}
                    className="redeem-btn"
                    style={{
                      backgroundColor: "#6455ED",
                      border: "none",
                      color: "#fff",
                      borderRadius: "25px",
                    }}
                  >
                    Redeem now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* <Footer
            aboveFooterText={
              customerId &&
              Number(customerId) === Number(process.env.REACT_APP_CUSTOMER_ID)
                ? "Enjoy choosing your Diwali gift - may this add to your festive joy!"
                : "🎉 Enjoy your swag pack, and please know that you are valued and appreciated"
            }
          /> */}
          <Footer
            footerText={checkoutDetails.footerText}
            footerColor={checkoutDetails.footerColor}
            customerId={checkoutDetails.customerId}
          />

          <PackModal
            open={open}
            onClose={closeModal}
            packDetails={showPackDetails}
            preSelectedProduct={preSelectedProduct}
            customerId={customerId}
          />
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
